package com.example.mystybag.login.views

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.platform.SoftwareKeyboardController
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.login.logic.MerchantResetPasswordViewModel
import com.example.mystybag.login.views.views.MerchantResetPasswordWebView
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

@Composable
fun MerchantResetPasswordView(viewModel: MerchantResetPasswordViewModel) {
    if (PlatformHelper.isWeb) {
        MerchantResetPasswordWebView(viewModel)
        return
    }

    val keyboardController = LocalSoftwareKeyboardController.current
    BottomSheetUIView(
        viewModel = viewModel,
        bottomSheetFactory = {}
    ) {
        MerchantResetPasswordForm(
            viewModel = viewModel,
            keyboardController = keyboardController,
            useScrollAndFixedButtonSpacing = false
        )
    }
}

@Composable
internal fun ColumnScope.MerchantResetPasswordForm(
    viewModel: MerchantResetPasswordViewModel,
    keyboardController: SoftwareKeyboardController?,
    useScrollAndFixedButtonSpacing: Boolean
) {
    val uiState by viewModel.uiState.subscribeAsState()
    val theme = AppTheme.current

    val columnModifier = if (useScrollAndFixedButtonSpacing) {
        Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = p16)
    } else {
        Modifier
            .fillMaxWidth()
            .weight(1f)
            .padding(horizontal = p16)
    }

    Column(
        modifier = columnModifier,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = p32, bottom = p24),
            contentAlignment = Alignment.CenterStart
        ) {
            Texts.DisplaySmall(
                text = stringResource(SR.strings.change_password),
                color = theme.colors.onSurface,
            )
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(
                p6,
                alignment = Alignment.CenterHorizontally
            )
        ) {
            DefaultCircleShapedTextField(
                modifier = Modifier.fillMaxWidth(),
                value = uiState.temporaryPassword,
                label = stringResource(SR.strings.temporary_password),
                errorText = uiState.temporaryPasswordErrorMsg,
                errorLeadingIcon = SRC.images.password_error_icon,
                onValueChange = viewModel::onTemporaryPasswordChange,
                isError = uiState.temporaryPasswordIsError,
                enabled = true,
                readOnly = false,
                visualTransformation = if (uiState.temporaryPasswordVisible) {
                    VisualTransformation.None
                } else {
                    PasswordVisualTransformation()
                },
                trailingIcon = {
                    Image(
                        painter = painterResource(
                            if (uiState.temporaryPasswordVisible) {
                                SRC.images.password_eye_open
                            } else {
                                SRC.images.password_eye_closed
                            }
                        ),
                        modifier = Modifier
                            .size(p24)
                            .noRippleClickable { viewModel.toggleTemporaryPasswordVisibility() }
                    )
                },
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Ascii,
                    imeAction = ImeAction.Next
                )
            )
        }

        Row(
            modifier = Modifier
                .padding(top = p16)
                .fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(
                p6,
                alignment = Alignment.CenterHorizontally
            )
        ) {
            DefaultCircleShapedTextField(
                modifier = Modifier.fillMaxWidth(),
                value = uiState.newPassword,
                label = stringResource(SR.strings.new_password),
                errorText = uiState.newPasswordErrorMsg,
                errorLeadingIcon = SRC.images.password_error_icon,
                onValueChange = viewModel::onNewPasswordChange,
                isError = uiState.newPasswordIsError,
                enabled = true,
                readOnly = false,
                visualTransformation = if (uiState.newPasswordVisible) {
                    VisualTransformation.None
                } else {
                    PasswordVisualTransformation()
                },
                trailingIcon = {
                    Image(
                        painter = painterResource(
                            if (uiState.newPasswordVisible) {
                                SRC.images.password_eye_open
                            } else {
                                SRC.images.password_eye_closed
                            }
                        ),
                        modifier = Modifier
                            .size(p24)
                            .noRippleClickable { viewModel.toggleNewPasswordVisibility() }
                    )
                },
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Ascii,
                    imeAction = ImeAction.Next
                )
            )
        }

        Row(
            modifier = Modifier
                .padding(top = p16)
                .fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(
                p6,
                alignment = Alignment.CenterHorizontally
            )
        ) {
            DefaultCircleShapedTextField(
                modifier = Modifier.fillMaxWidth(),
                value = uiState.confirmPassword,
                label = stringResource(SR.strings.confirm_password),
                errorText = uiState.confirmPasswordErrorMsg,
                errorLeadingIcon = SRC.images.password_error_icon,
                onValueChange = viewModel::onConfirmPasswordChange,
                isError = uiState.confirmPasswordIsError,
                enabled = true,
                readOnly = false,
                visualTransformation = if (uiState.confirmPasswordVisible) {
                    VisualTransformation.None
                } else {
                    PasswordVisualTransformation()
                },
                trailingIcon = {
                    Image(
                        painter = painterResource(
                            if (uiState.confirmPasswordVisible) {
                                SRC.images.password_eye_open
                            } else {
                                SRC.images.password_eye_closed
                            }
                        ),
                        modifier = Modifier
                            .size(p24)
                            .noRippleClickable { viewModel.toggleConfirmPasswordVisibility() }
                    )
                },
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Ascii,
                    imeAction = ImeAction.Done
                )
            )
        }

        if (useScrollAndFixedButtonSpacing) {
            Spacer(modifier = Modifier.height(p32))
        } else {
            Spacer(Modifier.weight(1f))
        }

        DefaultButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = p8),
            caption = stringResource(SR.strings.save),
            callback = {
                keyboardController?.hide()
                viewModel.onContinue()
            },
            identifier = "Merchant Reset Password Continue"
        )
    }
}
